import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, Search, Users } from 'lucide-react';
import { toast } from 'react-toastify';
import { communityAPI } from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';

const SuperAdminCommunityPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, newLast7Days: 0 });

  const fetchWaitlist = useCallback(async (searchValue = '') => {
    try {
      const response = await communityAPI.getWaitlist({
        q: searchValue,
        limit: 200,
        offset: 0
      });
      setItems(response?.data?.items || []);
      setStats(response?.data?.stats || { total: 0, newLast7Days: 0 });
    } catch (error) {
      toast.error(t('superadmin.community.errors.load', 'Unable to load waitlist emails'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    fetchWaitlist('');
  }, [fetchWaitlist]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWaitlist(query);
  };

  const filteredItems = useMemo(() => items, [items]);

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-[70vh]">
          <div className="flex items-center gap-3 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>{t('common.loading', 'Loading...')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-600" />
            {t('superadmin.community.title', 'Community waitlist')}
          </h1>
          <p className="text-sm text-gray-600">
            {t('superadmin.community.subtitle', 'Emails collected from the public launch banner')}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-xs px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-indigo-200 inline-flex items-center gap-1.5 self-start"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {t('common.refresh', 'Refresh')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.community.stats.total', 'Total emails')}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{stats.total || 0}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.community.stats.newLast7Days', 'Last 7 days')}</div>
          <div className="text-2xl font-semibold text-indigo-600 mt-1">{stats.newLast7Days || 0}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="relative w-full md:max-w-md">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  fetchWaitlist(event.currentTarget.value.trim());
                }
              }}
              placeholder={t('superadmin.community.searchPlaceholder', 'Search email...')}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
            />
          </div>
          <button
            type="button"
            onClick={() => fetchWaitlist(query.trim())}
            className="inline-flex items-center justify-center rounded-lg border border-indigo-200 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
          >
            {t('common.search', 'Search')}
          </button>
        </div>

        {filteredItems.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center">
            <Mail className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">{t('superadmin.community.empty', 'No email found')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t('superadmin.community.table.email', 'Email')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t('superadmin.community.table.language', 'Language')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t('superadmin.community.table.source', 'Source')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t('superadmin.community.table.createdAt', 'Created')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredItems.map((item) => (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 uppercase">{item.language || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.source || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.created_at)}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminCommunityPage;
